import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import RecoveryCodeModal from "../components/RecoveryCodeModal";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AccountSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [newCode, setNewCode] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/me/recovery-code/rotate", {
        current_password: currentPw,
      });
      setShowConfirm(false);
      setCurrentPw("");
      setNewCode(data.recovery_code);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-2xl px-6 py-10">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
          {t("account.eyebrow")}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          {t("account.title")}
        </h1>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="account-info">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("account.email_label")}
              </dt>
              <dd className="mt-0.5 text-slate-900" data-testid="account-email">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("account.role_label")}
              </dt>
              <dd className="mt-0.5 text-slate-900" data-testid="account-role">
                {t(`dashboard.role_badge_${user?.role}`)}
              </dd>
            </div>
          </dl>
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="account-recovery-section">
          <h2 className="text-base font-semibold text-slate-900">{t("recovery.settings_title")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("recovery.settings_body")}</p>

          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="mt-4 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            data-testid="recovery-regenerate-btn"
          >
            {t("recovery.regenerate")}
          </button>
        </section>
      </main>

      {showConfirm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" role="dialog" aria-modal="true" data-testid="recovery-regen-modal">
          <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">{t("recovery.regen_modal_title")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("recovery.regen_modal_body")}</p>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              {t("recovery.current_password")}
            </label>
            <input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="recovery-regen-pw-input"
            />
            {error && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="recovery-regen-error">{error}</div>}
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => { setShowConfirm(false); setCurrentPw(""); setError(""); }} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="recovery-regen-cancel">
                {t("common.cancel")}
              </button>
              <button type="submit" disabled={submitting} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60" data-testid="recovery-regen-submit">
                {submitting ? t("common.loading") : t("recovery.regen_submit")}
              </button>
            </div>
          </form>
        </div>
      )}

      {newCode && (
        <RecoveryCodeModal code={newCode} variant="signup" onClose={() => setNewCode(null)} />
      )}
    </div>
  );
}
